import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import DI from "../utility/DI";
import { CircularProgress } from "@mui/material";
import Footer from "../utility/Footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MuiOtpInput } from "mui-one-time-password-input";

const VerifyEmail = (props) => {
  const {
    di: {
      success,
      error,
      POST,
      urls,
      contextData: { setState },
    },
  } = props;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const [otp, setOtp] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [countdown, setCountdown] = React.useState(0);

  React.useEffect(() => {
    if (!email) {
      navigate("/signup");
    }
  }, [email, navigate]);

  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleOtpChange = (newValue) => {
    setOtp(newValue);
  };

  const handleVerify = () => {
    if (otp.length !== 6) {
      error("Please enter a 6-digit OTP.");
      return;
    }

    setLoading(true);
    POST(urls.post.verify_email, { email, otp })
      .then((res) => {
        if (res.success) {
          // Auto-login: set session with returned user data
          setState((prev) => ({ ...prev, session: res.user }));
          success("Email verified! Welcome aboard.");
          // Navigate to home or panel
          if (res.user?.homes?.length) {
            navigate("/home");
          } else {
            navigate("/panel");
          }
        } else {
          error(res.message);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        error("Something went wrong. Try again.");
      });
  };

  const handleResendOtp = () => {
    setResendLoading(true);
    POST(urls.post.resend_otp, { email })
      .then((res) => {
        if (res.success) {
          success("New OTP sent to your email.");
          setCountdown(30);
          setOtp("");
        } else {
          error(res.message);
        }
        setResendLoading(false);
      })
      .catch(() => {
        setResendLoading(false);
        error("Failed to resend OTP.");
      });
  };

  const handleComplete = (finalValue) => {
    setOtp(finalValue);
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div style={{ width: "80px" }}>
          <img
            style={{ width: "100%" }}
            src={"/transparent_logo.png"}
            alt={"spent_app"}
            loading="lazy"
          />
        </div>
        <Typography component="h1" variant="h5" sx={{ mt: 2 }}>
          Verify Your Email
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ mt: 1, mb: 3, px: 2 }}
        >
          We've sent a 6-digit OTP to <strong>{email}</strong>. Enter it below
          to verify your account.
        </Typography>

        <Box sx={{ width: "100%", px: 2 }}>
          <MuiOtpInput
            value={otp}
            onChange={handleOtpChange}
            onComplete={handleComplete}
            length={6}
            autoFocus
            TextFieldsProps={{
              placeholder: "0",
              inputProps: {
                "data-testid": "otp-input",
              },
            }}
            sx={{
              gap: 1,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "primary.main",
                },
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleVerify}
            disabled={loading || otp.length !== 6}
            sx={{ mt: 3, mb: 1 }}
            id="verify-otp-btn"
          >
            {loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              "Verify Email"
            )}
          </Button>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {countdown > 0 ? (
                <>
                  Resend in{" "}
                  <strong style={{ color: "orange" }}>{countdown}s</strong>
                </>
              ) : (
                "Didn't receive OTP?"
              )}
            </Typography>
            <Button
              size="small"
              onClick={handleResendOtp}
              disabled={countdown > 0 || resendLoading}
              id="resend-otp-btn"
            >
              {resendLoading ? (
                <CircularProgress color="inherit" size={16} />
              ) : (
                "Resend OTP"
              )}
            </Button>
          </Box>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Link
              onClick={() => navigate("/signup")}
              style={{ cursor: "pointer" }}
              variant="body2"
            >
              ← Back to Sign Up
            </Link>
          </Box>
        </Box>
      </Box>
      <div style={{ marginTop: "20px" }}>
        <Footer />
      </div>
    </Container>
  );
};

export default DI(VerifyEmail);
